import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, EmptyState, StatCard } from "@/components/Common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Building2, Plus, Pencil, Trash2, Landmark, Search, Sparkles, ShieldCheck,
  Users, Receipt, FileText, ArrowDownLeft, ArrowUpRight, ScrollText,
  CalendarClock, Download, Eye, CheckCircle2, AlertTriangle, Clock, Stamp,
} from "lucide-react";
import {
  isCorporateType, ROC_CODES, COMPANY_CLASSES, COMPANY_CATEGORIES, COMPANY_SUBCATEGORIES,
  COMPANY_STATUSES, DIRECTOR_DESIGNATIONS, TXN_TYPES, txnTypeLabel, fmtINR, fmtDate, CURRENCIES,
} from "@/lib/format";

const primaryBtn = "bg-[color:var(--mk-primary)] text-[#0B1220] hover:bg-[color:var(--mk-primary)]/90 font-medium";
const surface = "border-[color:var(--mk-border)] bg-[color:var(--mk-surface)]";
const dialogSurface = "border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)]";

const ROLE_LABELS = { director: "Director", shareholder: "Shareholder", beneficial_owner: "Beneficial Owner" };
const ROLE_COLORS = { director: "#7FA8D9", shareholder: "#C9A86A", beneficial_owner: "#6FB89A" };

const emptyProfile = {
  cin: "", pan: "", gstin: "", roc_code: "", registration_number: "", incorporation_date: "",
  company_class: "Private", company_category: "", company_subcategory: "", company_status: "Active",
  is_nbfc: false, rbi_registration_no: "", nbfc_layer: "", authorized_capital: 0, paid_up_capital: 0,
  registered_office: "", email: "", listed: false, last_agm_date: "", balance_sheet_date: "", notes: "",
};

export default function Corporate() {
  const [params, setParams] = useSearchParams();
  const [corps, setCorps] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(params.get("entity") || null);
  const [query, setQuery] = useState("");

  const loadBase = useCallback(async () => {
    setLoading(true);
    try {
      const [e, m] = await Promise.all([api.get("/entities"), api.get("/members")]);
      const corpList = (e.data || []).filter((x) => isCorporateType(x.type));
      setCorps(corpList); setMembers(m.data || []);
      setSelectedId((prev) => prev || (corpList[0] && corpList[0].id) || null);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { loadBase(); }, [loadBase]);

  const selected = corps.find((c) => c.id === selectedId) || null;
  const filtered = corps.filter((c) => c.name.toLowerCase().includes(query.toLowerCase()));

  const selectEntity = (id) => { setSelectedId(id); setParams(id ? { entity: id } : {}); };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Corporate Entities" subtitle="MCA profiles, directors & shareholders, and the corporate transaction ledger" />

      {loading ? (
        <div className="grid gap-4 lg:grid-cols-[280px_1fr]"><Skeleton className="h-96 rounded-[18px]" /><Skeleton className="h-96 rounded-[18px]" /></div>
      ) : corps.length === 0 ? (
        <Card className={surface}><EmptyState icon={Building2} title="No corporate entities yet" description="Create an entity of type Private Limited, HoldCo, OpCo, NBFC or LLP from Entities & Family — it will appear here for MCA, directors and ledger management." /></Card>
      ) : (
        <div className="grid gap-5 lg:grid-cols-[280px_1fr]">
          {/* Master list */}
          <aside className="space-y-3">
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[color:var(--mk-muted)]" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find company…" className="pl-9" data-testid="corporate-search-input" />
            </div>
            <div className="space-y-1.5" data-testid="corporate-entity-list">
              {filtered.map((c) => {
                const active = c.id === selectedId;
                return (
                  <button key={c.id} onClick={() => selectEntity(c.id)} data-testid={`corporate-list-item-${c.id}`}
                    className={`group flex w-full items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-[background-color,border-color] duration-150 ${active ? "border-[color:var(--mk-primary)]/50 bg-[rgba(176,141,87,0.12)]" : "border-[color:var(--mk-border)] bg-[color:var(--mk-surface)] hover:border-[color:var(--mk-border-strong)]"}`}>
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[rgba(176,141,87,0.14)]"><Landmark className="h-4 w-4 text-[color:var(--mk-primary-2)]" strokeWidth={2.25} /></span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium text-[color:var(--mk-text)]">{c.name}</span>
                      <span className="block truncate text-[11px] text-[color:var(--mk-muted)]">{c.type}</span>
                    </span>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Detail */}
          {selected ? (
            <CorporateDetail key={selected.id} entity={selected} members={members} corps={corps} />
          ) : (
            <Card className={surface}><EmptyState icon={Building2} title="Select a company" description="Choose a corporate entity from the list to manage its profile, stakeholders and ledger." /></Card>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------------- Detail (Profile / Stakeholders / Ledger) ---------------- */
function CorporateDetail({ entity, members, corps }) {
  const [profile, setProfile] = useState(null);
  const [hasProfile, setHasProfile] = useState(false);
  const [stakeholders, setStakeholders] = useState([]);
  const [ledger, setLedger] = useState({ transactions: [], summary: {} });
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pRes, sRes, lRes] = await Promise.allSettled([
        api.get(`/corporate/profiles/${entity.id}`),
        api.get("/corporate/stakeholders", { params: { entity_id: entity.id } }),
        api.get("/corporate/ledger", { params: { entity_id: entity.id } }),
      ]);
      if (pRes.status === "fulfilled") { setProfile(pRes.value.data); setHasProfile(true); }
      else { setProfile({ ...emptyProfile }); setHasProfile(false); }
      setStakeholders(sRes.status === "fulfilled" ? sRes.value.data : []);
      setLedger(lRes.status === "fulfilled" ? lRes.value.data : { transactions: [], summary: {} });
    } finally { setLoading(false); }
  }, [entity.id]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <Skeleton className="h-[520px] rounded-[18px]" />;

  return (
    <Card className={`${surface} overflow-hidden`}>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-[color:var(--mk-border)] px-5 py-4">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[rgba(176,141,87,0.14)]"><Building2 className="h-5 w-5 text-[color:var(--mk-primary-2)]" strokeWidth={2.25} /></span>
          <div>
            <h2 className="font-display text-xl font-semibold text-[color:var(--mk-text)]" data-testid="corporate-detail-name">{entity.name}</h2>
            <div className="flex items-center gap-2 text-[11px] text-[color:var(--mk-muted)]">
              <span>{entity.type}</span>
              {hasProfile && profile?.cin && <span className="font-data">· {profile.cin}</span>}
              {hasProfile && <Badge className="border-[rgba(15,61,46,0.55)] bg-[rgba(15,61,46,0.35)] text-[color:var(--mk-text)]">{profile?.company_status || "Active"}</Badge>}
            </div>
          </div>
        </div>
      </div>

      <div className="p-5">
        <Tabs defaultValue="profile">
          <TabsList className="mb-5">
            <TabsTrigger value="profile" data-testid="corp-tab-profile"><FileText className="mr-2 h-4 w-4" /> Profile</TabsTrigger>
            <TabsTrigger value="stakeholders" data-testid="corp-tab-stakeholders"><Users className="mr-2 h-4 w-4" /> Directors & Shareholders ({stakeholders.length})</TabsTrigger>
            <TabsTrigger value="ledger" data-testid="corp-tab-ledger"><Receipt className="mr-2 h-4 w-4" /> Ledger ({(ledger.transactions || []).length})</TabsTrigger>
            <TabsTrigger value="compliance" data-testid="corp-tab-compliance"><ShieldCheck className="mr-2 h-4 w-4" /> Statutory</TabsTrigger>
          </TabsList>

          <TabsContent value="profile">
            <ProfileTab entity={entity} profile={profile} hasProfile={hasProfile} onSaved={load} />
          </TabsContent>
          <TabsContent value="stakeholders">
            <StakeholdersTab entity={entity} members={members} stakeholders={stakeholders} onChanged={load} />
          </TabsContent>
          <TabsContent value="ledger">
            <LedgerTab entity={entity} corps={corps} ledger={ledger} onChanged={load} />
          </TabsContent>
          <TabsContent value="compliance">
            <ComplianceTab entity={entity} />
          </TabsContent>
        </Tabs>
      </div>
    </Card>
  );
}

/* ---------------- Profile tab ---------------- */
function ProfileTab({ entity, profile, hasProfile, onSaved }) {
  const [form, setForm] = useState({ ...emptyProfile, ...profile });
  const [saving, setSaving] = useState(false);
  const [fetching, setFetching] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => { setForm({ ...emptyProfile, ...profile }); }, [profile]);

  const fetchMca = async () => {
    if (!form.cin || form.cin.length < 8) { toast.error("Enter a CIN to fetch MCA data"); return; }
    setFetching(true);
    try {
      const { data } = await api.post("/corporate/mca/lookup", { cin: form.cin });
      setForm((f) => ({ ...f, ...data.data }));
      toast.info(data.message || "MCA data loaded (demo)");
    } catch (e) { toast.error(e?.response?.data?.detail || "Lookup failed"); }
    finally { setFetching(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post("/corporate/profiles", { entity_id: entity.id, ...form, authorized_capital: Number(form.authorized_capital) || 0, paid_up_capital: Number(form.paid_up_capital) || 0 });
      toast.success("Corporate profile saved");
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5" data-testid="corporate-profile-tab">
      {/* MCA lookup banner */}
      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-[rgba(176,141,87,0.3)] bg-[rgba(176,141,87,0.08)] px-4 py-3">
        <Sparkles className="h-4 w-4 text-[color:var(--mk-primary-2)]" />
        <span className="text-sm text-[color:var(--mk-text-2)]">Fetch master data from MCA by CIN. <span className="text-[color:var(--mk-muted)]">Demo data for now — live SurePass lookup connects later.</span></span>
        <Button size="sm" variant="outline" className="ml-auto border-[color:var(--mk-primary)]/40 text-[color:var(--mk-primary-2)] hover:bg-[rgba(176,141,87,0.12)]" onClick={fetchMca} disabled={fetching} data-testid="mca-fetch-button">
          <Search className="mr-1.5 h-3.5 w-3.5" /> {fetching ? "Fetching…" : "Fetch from MCA"}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Field label="CIN"><Input value={form.cin || ""} onChange={(e) => set("cin", e.target.value.toUpperCase())} placeholder="U67190MH2012PTC123456" className="font-data" data-testid="profile-cin-input" /></Field>
        <Field label="PAN"><Input value={form.pan || ""} onChange={(e) => set("pan", e.target.value.toUpperCase())} className="font-data" /></Field>
        <Field label="GSTIN"><Input value={form.gstin || ""} onChange={(e) => set("gstin", e.target.value.toUpperCase())} className="font-data" /></Field>
        <Field label="Registrar of Companies"><SelectBox value={form.roc_code} onChange={(v) => set("roc_code", v)} options={ROC_CODES} placeholder="Select RoC" /></Field>
        <Field label="Registration Number"><Input value={form.registration_number || ""} onChange={(e) => set("registration_number", e.target.value)} /></Field>
        <Field label="Incorporation Date"><Input type="date" value={form.incorporation_date ? String(form.incorporation_date).slice(0, 10) : ""} onChange={(e) => set("incorporation_date", e.target.value)} /></Field>
        <Field label="Company Class"><SelectBox value={form.company_class} onChange={(v) => set("company_class", v)} options={COMPANY_CLASSES} /></Field>
        <Field label="Company Category"><SelectBox value={form.company_category} onChange={(v) => set("company_category", v)} options={COMPANY_CATEGORIES} placeholder="Select category" /></Field>
        <Field label="Sub-category"><SelectBox value={form.company_subcategory} onChange={(v) => set("company_subcategory", v)} options={COMPANY_SUBCATEGORIES} placeholder="Select" /></Field>
        <Field label="Company Status"><SelectBox value={form.company_status} onChange={(v) => set("company_status", v)} options={COMPANY_STATUSES} /></Field>
        <Field label="Authorized Capital (₹)"><Input type="number" value={form.authorized_capital} onChange={(e) => set("authorized_capital", e.target.value)} className="font-data" /></Field>
        <Field label="Paid-up Capital (₹)"><Input type="number" value={form.paid_up_capital} onChange={(e) => set("paid_up_capital", e.target.value)} className="font-data" /></Field>
        <Field label="Last AGM Date"><Input type="date" value={form.last_agm_date ? String(form.last_agm_date).slice(0, 10) : ""} onChange={(e) => set("last_agm_date", e.target.value)} /></Field>
        <Field label="Balance Sheet Date"><Input type="date" value={form.balance_sheet_date ? String(form.balance_sheet_date).slice(0, 10) : ""} onChange={(e) => set("balance_sheet_date", e.target.value)} /></Field>
        <Field label="Email"><Input type="email" value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></Field>
        <Field label="Registered Office" className="sm:col-span-2 lg:col-span-3"><Textarea rows={2} value={form.registered_office || ""} onChange={(e) => set("registered_office", e.target.value)} /></Field>
      </div>

      {/* NBFC + listed toggles */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <div className="flex items-center justify-between rounded-xl border border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] px-4 py-3">
          <div><div className="text-sm font-medium text-[color:var(--mk-text)]">NBFC</div><div className="text-[11px] text-[color:var(--mk-muted)]">RBI-registered non-banking finance co.</div></div>
          <Switch checked={!!form.is_nbfc} onCheckedChange={(v) => set("is_nbfc", v)} data-testid="profile-nbfc-switch" />
        </div>
        {form.is_nbfc && (
          <>
            <Field label="RBI Registration No."><Input value={form.rbi_registration_no || ""} onChange={(e) => set("rbi_registration_no", e.target.value)} /></Field>
            <Field label="NBFC Layer"><SelectBox value={form.nbfc_layer} onChange={(v) => set("nbfc_layer", v)} options={["Base Layer", "Middle Layer", "Upper Layer", "Top Layer"]} placeholder="Select layer" /></Field>
          </>
        )}
        <div className="flex items-center justify-between rounded-xl border border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)] px-4 py-3">
          <div><div className="text-sm font-medium text-[color:var(--mk-text)]">Listed</div><div className="text-[11px] text-[color:var(--mk-muted)]">Securities listed on an exchange</div></div>
          <Switch checked={!!form.listed} onCheckedChange={(v) => set("listed", v)} />
        </div>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} className={primaryBtn} data-testid="save-profile-button"><ShieldCheck className="mr-2 h-4 w-4" /> {saving ? "Saving…" : hasProfile ? "Update Profile" : "Save Profile"}</Button>
      </div>
    </div>
  );
}

/* ---------------- Stakeholders tab ---------------- */
const emptyStakeholder = { role: "director", member_id: "", name: "", din: "", designation: "", pct: "", shares_held: "", appointed_on: "" };

function StakeholdersTab({ entity, members, stakeholders, onChanged }) {
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ ...emptyStakeholder });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const totalPct = stakeholders.filter((s) => s.role !== "director").reduce((a, s) => a + (Number(s.pct) || 0), 0);

  const openDialog = (s) => {
    setEdit(s);
    setForm(s ? { role: s.role, member_id: s.member_id || "", name: s.name || "", din: s.din || "", designation: s.designation || "", pct: s.pct || "", shares_held: s.shares_held || "", appointed_on: s.appointed_on ? String(s.appointed_on).slice(0, 10) : "" } : { ...emptyStakeholder });
    setOpen(true);
  };

  const save = async () => {
    const isOwner = form.role !== "director";
    if (isOwner && !form.member_id) { toast.error("Shareholders & beneficial owners must be tagged to a family member"); return; }
    if (isOwner && !(Number(form.pct) > 0 && Number(form.pct) <= 100)) { toast.error("Enter an ownership % between 0 and 100"); return; }
    if (!form.member_id && !form.name.trim()) { toast.error("Tag a member or enter a name"); return; }
    const payload = {
      entity_id: entity.id, role: form.role,
      member_id: form.member_id || null, name: form.name || null,
      din: form.din || null, designation: form.designation || null,
      pct: Number(form.pct) || 0, shares_held: form.shares_held ? Number(form.shares_held) : null,
      appointed_on: form.appointed_on || null,
    };
    try {
      if (edit) await api.put(`/corporate/stakeholders/${edit.id}`, payload);
      else await api.post("/corporate/stakeholders", payload);
      toast.success(edit ? "Stakeholder updated" : "Stakeholder added");
      setOpen(false); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const remove = async (s) => {
    try { await api.delete(`/corporate/stakeholders/${s.id}`); toast.success("Stakeholder removed"); onChanged(); }
    catch (e) { toast.error("Could not delete"); }
  };

  const isOwnerRole = form.role !== "director";

  return (
    <div className="space-y-4" data-testid="corporate-stakeholders-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[color:var(--mk-text-2)]">
          Shareholders & beneficial owners tagged to family members feed the <span className="text-[color:var(--mk-primary-2)]">Ownership graph</span>. Total ownership tagged: <span className="font-data text-[color:var(--mk-text)]">{totalPct.toFixed(1)}%</span>
        </p>
        <Button size="sm" onClick={() => openDialog(null)} className={primaryBtn} data-testid="add-stakeholder-button"><Plus className="mr-1.5 h-4 w-4" /> Add Stakeholder</Button>
      </div>

      {stakeholders.length === 0 ? (
        <Card className="border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)]"><EmptyState icon={Users} title="No stakeholders yet" description="Add directors, shareholders and beneficial owners. Owners with a % automatically appear in the ownership graph." /></Card>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[color:var(--mk-border)]">
          <table className="w-full text-sm" data-testid="stakeholders-table">
            <thead className="bg-[rgba(16,27,43,0.6)]">
              <tr className="text-left text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--mk-text-2)]">
                <th className="px-4 py-3">Name</th><th className="px-4 py-3">Role</th><th className="px-4 py-3">Designation / DIN</th>
                <th className="px-4 py-3 text-right">Ownership %</th><th className="px-4 py-3">Graph</th><th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {stakeholders.map((s) => (
                <tr key={s.id} className="border-t border-[color:var(--mk-border)] hover:bg-[rgba(176,141,87,0.06)]" data-testid={`stakeholder-row-${s.id}`}>
                  <td className="px-4 py-3 font-medium text-[color:var(--mk-text)]">{s.display_name}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className="border-[color:var(--mk-border-strong)]" style={{ color: ROLE_COLORS[s.role] }}>{ROLE_LABELS[s.role]}</Badge></td>
                  <td className="px-4 py-3 text-[color:var(--mk-text-2)]">{s.designation || "—"}{s.din ? <span className="ml-1 font-data text-[11px] text-[color:var(--mk-muted)]">· DIN {s.din}</span> : ""}</td>
                  <td className="px-4 py-3 text-right font-data tabular-nums text-[color:var(--mk-text)]">{s.role === "director" && !(Number(s.pct) > 0) ? "—" : `${Number(s.pct).toFixed(2)}%`}</td>
                  <td className="px-4 py-3">{s.synced_edge_id ? <Badge className="border-[rgba(15,61,46,0.55)] bg-[rgba(15,61,46,0.35)] text-[color:var(--mk-text)]">Linked</Badge> : <span className="text-[11px] text-[color:var(--mk-muted)]">—</span>}</td>
                  <td className="px-4 py-3"><div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[color:var(--mk-text-2)] hover:text-[color:var(--mk-text)]" onClick={() => openDialog(s)} data-testid={`edit-stakeholder-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                    <AlertDialog><AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-[#D98A8A] hover:text-[#D98A8A]" data-testid={`delete-stakeholder-${s.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent className={dialogSurface}><AlertDialogHeader><AlertDialogTitle>Remove stakeholder?</AlertDialogTitle><AlertDialogDescription>Removing {s.display_name} will also remove any auto-synced ownership link.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(s)} className="bg-[#7A2E38] hover:bg-[#7A2E38]/90">Remove</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                    </AlertDialog>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={`max-w-lg ${dialogSurface}`}>
          <DialogHeader><DialogTitle className="font-display">{edit ? "Edit Stakeholder" : "Add Stakeholder"}</DialogTitle><DialogDescription>Directors, shareholders & beneficial owners. Owners are linked to the ownership graph.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
            <Field label="Role"><SelectBox value={form.role} onChange={(v) => set("role", v)} options={[{ value: "director", label: "Director" }, { value: "shareholder", label: "Shareholder" }, { value: "beneficial_owner", label: "Beneficial Owner" }]} testid="stakeholder-role-select" /></Field>
            <Field label={isOwnerRole ? "Family Member (required)" : "Family Member (optional)"}>
              <SelectBox value={form.member_id} onChange={(v) => set("member_id", v === "__none" ? "" : v)} placeholder="Tag a member" testid="stakeholder-member-select"
                options={[{ value: "__none", label: "— Not a family member —" }, ...members.map((m) => ({ value: m.id, label: m.name }))]} />
            </Field>
            {!form.member_id && <Field label="Name (if not a member)" className="sm:col-span-2"><Input value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="e.g. External Nominee Director" data-testid="stakeholder-name-input" /></Field>}
            {form.role === "director" && <Field label="Designation"><SelectBox value={form.designation} onChange={(v) => set("designation", v)} options={DIRECTOR_DESIGNATIONS} placeholder="Select" /></Field>}
            {form.role === "director" && <Field label="DIN"><Input value={form.din} onChange={(e) => set("din", e.target.value)} className="font-data" /></Field>}
            {isOwnerRole && <Field label="Ownership %"><Input type="number" value={form.pct} onChange={(e) => set("pct", e.target.value)} placeholder="e.g. 51" className="font-data" data-testid="stakeholder-pct-input" /></Field>}
            {isOwnerRole && <Field label="Shares Held (optional)"><Input type="number" value={form.shares_held} onChange={(e) => set("shares_held", e.target.value)} className="font-data" /></Field>}
            <Field label="Appointed / Acquired On"><Input type="date" value={form.appointed_on} onChange={(e) => set("appointed_on", e.target.value)} /></Field>
          </div>
          <DialogFooter><Button variant="outline" className="border-[color:var(--mk-border)]" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className={primaryBtn} data-testid="save-stakeholder-button">{edit ? "Save" : "Add"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Ledger tab ---------------- */
const emptyTxn = { date: "", txn_type: "income", direction: "inflow", amount: "", currency: "INR", counterparty_entity_id: "", reference: "", description: "" };

function LedgerTab({ entity, corps, ledger, onChanged }) {
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [form, setForm] = useState({ ...emptyTxn });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const s = ledger.summary || {};
  const rows = ledger.transactions || [];

  const openDialog = (t) => {
    setEdit(t);
    setForm(t ? { date: t.date ? String(t.date).slice(0, 10) : "", txn_type: t.txn_type, direction: t.direction, amount: t.amount, currency: t.currency || "INR", counterparty_entity_id: t.counterparty_entity_id || "", reference: t.reference || "", description: t.description || "" } : { ...emptyTxn, date: new Date().toISOString().slice(0, 10) });
    setOpen(true);
  };

  const save = async () => {
    if (!form.date) { toast.error("Pick a date"); return; }
    if (!(Number(form.amount) > 0)) { toast.error("Amount must be greater than zero"); return; }
    if (form.txn_type === "inter_entity_transfer" && !form.counterparty_entity_id) { toast.error("Select a counterparty entity"); return; }
    const payload = { entity_id: entity.id, date: form.date, txn_type: form.txn_type, direction: form.direction, amount: Number(form.amount), currency: form.currency, counterparty_entity_id: form.counterparty_entity_id || null, reference: form.reference || null, description: form.description || null };
    try {
      if (edit) await api.put(`/corporate/ledger/${edit.id}`, payload);
      else await api.post("/corporate/ledger", payload);
      toast.success(edit ? "Transaction updated" : "Transaction added");
      setOpen(false); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
  };

  const remove = async (t) => { try { await api.delete(`/corporate/ledger/${t.id}`); toast.success("Transaction removed"); onChanged(); } catch { toast.error("Could not delete"); } };

  return (
    <div className="space-y-4" data-testid="corporate-ledger-tab">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard label="Total Inflow" value={fmtINR(s.total_inflow || 0, { compact: true })} icon={ArrowDownLeft} accent="#6FB89A" testid="ledger-inflow" />
        <StatCard label="Total Outflow" value={fmtINR(s.total_outflow || 0, { compact: true })} icon={ArrowUpRight} accent="#C98E6E" testid="ledger-outflow" />
        <StatCard label="Net Balance" value={fmtINR(s.net_balance || 0, { compact: true })} icon={ScrollText} accent="#C9A86A" testid="ledger-net" />
      </div>

      <div className="flex justify-end">
        <Button size="sm" onClick={() => openDialog(null)} className={primaryBtn} data-testid="add-transaction-button"><Plus className="mr-1.5 h-4 w-4" /> Add Transaction</Button>
      </div>

      {rows.length === 0 ? (
        <Card className="border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)]"><EmptyState icon={Receipt} title="No transactions yet" description="Record capital infusions, investments, income, expenses and inter-entity transfers to build a running balance." /></Card>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[color:var(--mk-border)]">
          <table className="w-full text-sm" data-testid="ledger-table">
            <thead className="bg-[rgba(16,27,43,0.6)]">
              <tr className="text-left text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--mk-text-2)]">
                <th className="px-4 py-3">Date</th><th className="px-4 py-3">Type</th><th className="px-4 py-3">Description</th>
                <th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3 text-right">Running Balance</th><th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr key={t.id} className="border-t border-[color:var(--mk-border)] hover:bg-[rgba(176,141,87,0.06)]" data-testid={`ledger-row-${t.id}`}>
                  <td className="px-4 py-3 font-data text-[color:var(--mk-text-2)]">{fmtDate(t.date)}</td>
                  <td className="px-4 py-3"><Badge variant="outline" className="border-[color:var(--mk-border-strong)] text-[color:var(--mk-text-2)]">{txnTypeLabel(t.txn_type)}</Badge></td>
                  <td className="px-4 py-3 text-[color:var(--mk-text-2)]">{t.description || t.counterparty_name || "—"}</td>
                  <td className={`px-4 py-3 text-right font-data tabular-nums ${t.direction === "inflow" ? "text-[#6FB89A]" : "text-[#C98E6E]"}`}>{t.direction === "inflow" ? "+" : "−"}{fmtINR(t.amount, { compact: true })}</td>
                  <td className="px-4 py-3 text-right font-data tabular-nums text-[color:var(--mk-text)]">{fmtINR(t.running_balance, { compact: true })}</td>
                  <td className="px-4 py-3"><div className="flex items-center justify-end gap-1">
                    <Button variant="ghost" size="icon" className="h-8 w-8 text-[color:var(--mk-text-2)] hover:text-[color:var(--mk-text)]" onClick={() => openDialog(t)} data-testid={`edit-transaction-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                    <AlertDialog><AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-[#D98A8A] hover:text-[#D98A8A]" data-testid={`delete-transaction-${t.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent className={dialogSurface}><AlertDialogHeader><AlertDialogTitle>Delete transaction?</AlertDialogTitle><AlertDialogDescription>This will recompute the running balance.</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => remove(t)} className="bg-[#7A2E38] hover:bg-[#7A2E38]/90">Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                    </AlertDialog>
                  </div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className={`max-w-lg ${dialogSurface}`}>
          <DialogHeader><DialogTitle className="font-display">{edit ? "Edit Transaction" : "Add Transaction"}</DialogTitle><DialogDescription>Record a corporate ledger entry for {entity.name}.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-1 gap-4 py-2 sm:grid-cols-2">
            <Field label="Date"><Input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} data-testid="txn-date-input" /></Field>
            <Field label="Type"><SelectBox value={form.txn_type} onChange={(v) => set("txn_type", v)} options={TXN_TYPES} testid="txn-type-select" /></Field>
            <Field label="Direction"><SelectBox value={form.direction} onChange={(v) => set("direction", v)} options={[{ value: "inflow", label: "Inflow (money in)" }, { value: "outflow", label: "Outflow (money out)" }]} testid="txn-direction-select" /></Field>
            <Field label="Amount (₹)"><Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="font-data" data-testid="txn-amount-input" /></Field>
            <Field label="Currency"><SelectBox value={form.currency} onChange={(v) => set("currency", v)} options={CURRENCIES} /></Field>
            {form.txn_type === "inter_entity_transfer" && (
              <Field label="Counterparty Entity"><SelectBox value={form.counterparty_entity_id} onChange={(v) => set("counterparty_entity_id", v)} placeholder="Select entity" options={corps.filter((c) => c.id !== entity.id).map((c) => ({ value: c.id, label: c.name }))} testid="txn-counterparty-select" /></Field>
            )}
            <Field label="Reference (optional)"><Input value={form.reference} onChange={(e) => set("reference", e.target.value)} /></Field>
            <Field label="Description" className="sm:col-span-2"><Textarea rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} /></Field>
          </div>
          <DialogFooter><Button variant="outline" className="border-[color:var(--mk-border)]" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className={primaryBtn} data-testid="save-transaction-button">{edit ? "Save" : "Add"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- Statutory compliance tab ---------------- */
const COMPLIANCE_STATUS = {
  filed: { label: "Filed", cls: "border-[rgba(15,61,46,0.55)] bg-[rgba(15,61,46,0.35)] text-[color:var(--mk-text)]", icon: CheckCircle2 },
  overdue: { label: "Overdue", cls: "border-[rgba(74,31,42,0.6)] bg-[rgba(74,31,42,0.4)] text-[#E7A0A0]", icon: AlertTriangle },
  due_soon: { label: "Due soon", cls: "border-[rgba(176,141,87,0.4)] bg-[rgba(176,141,87,0.2)] text-[color:var(--mk-text)]", icon: Clock },
  upcoming: { label: "Upcoming", cls: "border-[color:var(--mk-border-strong)] text-[color:var(--mk-text-2)]", icon: CalendarClock },
};

function ComplianceTab({ entity }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/corporate/${entity.id}/compliance`);
      setData(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load compliance"); }
    finally { setLoading(false); }
  }, [entity.id]);
  useEffect(() => { load(); }, [load]);

  const openPreview = async (code) => {
    try {
      const { data } = await api.get(`/corporate/${entity.id}/eform/${encodeURIComponent(code)}/preview`);
      setPreview(data); setPreviewOpen(true);
    } catch (e) { toast.error("Could not load preview"); }
  };

  const downloadPdf = async (code) => {
    setDownloading(code);
    try {
      const res = await api.get(`/corporate/${entity.id}/eform/${encodeURIComponent(code)}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `DRAFT_${code.replace(/[ /]/g, "_")}_${entity.name.replace(/ /g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success(`DRAFT ${code} downloaded`);
    } catch (e) { toast.error("Could not generate PDF"); }
    finally { setDownloading(null); }
  };

  const toggleFiled = async (item) => {
    try {
      await api.post(`/corporate/${entity.id}/compliance/${encodeURIComponent(item.code)}/mark`, { filed: !item.filed, filed_date: new Date().toISOString().slice(0, 10) });
      toast.success(item.filed ? "Marked as pending" : "Marked as filed");
      load();
    } catch (e) { toast.error("Could not update"); }
  };

  if (loading) return <Skeleton className="h-96 rounded-[18px]" />;
  const items = data?.items || [];
  const counts = items.reduce((a, i) => ({ ...a, [i.status]: (a[i.status] || 0) + 1 }), {});

  return (
    <div className="space-y-4" data-testid="corporate-compliance-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-[color:var(--mk-text-2)]">
          Statutory calendar for <span className="font-data text-[color:var(--mk-text)]">{data?.financial_year}</span>. E-forms are <span className="text-[color:var(--mk-primary-2)]">DRAFT mock-ups</span> for internal preparation — not for filing.
        </p>
        <div className="flex gap-1.5">
          {counts.overdue ? <Badge className={COMPLIANCE_STATUS.overdue.cls}>{counts.overdue} overdue</Badge> : null}
          {counts.due_soon ? <Badge className={COMPLIANCE_STATUS.due_soon.cls}>{counts.due_soon} due soon</Badge> : null}
          {counts.filed ? <Badge className={COMPLIANCE_STATUS.filed.cls}>{counts.filed} filed</Badge> : null}
        </div>
      </div>

      {items.length === 0 ? (
        <Card className="border-[color:var(--mk-border)] bg-[color:var(--mk-surface-2)]"><EmptyState icon={ShieldCheck} title="No statutory items" description="Add a corporate profile (and mark the entity type) to generate the applicable statutory calendar." /></Card>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-[color:var(--mk-border)]">
          <table className="w-full text-sm" data-testid="compliance-table">
            <thead className="bg-[rgba(16,27,43,0.6)]">
              <tr className="text-left text-xs font-semibold uppercase tracking-[0.14em] text-[color:var(--mk-text-2)]">
                <th className="px-4 py-3">Form</th><th className="px-4 py-3">Authority</th>
                <th className="px-4 py-3">Due date</th><th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">E-form (DRAFT)</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => {
                const sc = COMPLIANCE_STATUS[it.status] || COMPLIANCE_STATUS.upcoming;
                const SIcon = sc.icon;
                return (
                  <tr key={it.code} className="border-t border-[color:var(--mk-border)] hover:bg-[rgba(176,141,87,0.06)]" data-testid={`compliance-row-${it.code}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-[color:var(--mk-text)]">{it.code}</div>
                      <div className="text-[11px] text-[color:var(--mk-muted)]">{it.title}</div>
                    </td>
                    <td className="px-4 py-3 text-[color:var(--mk-text-2)] text-xs">{it.authority}</td>
                    <td className="px-4 py-3 font-data text-[color:var(--mk-text-2)]">{fmtDate(it.due_date)}</td>
                    <td className="px-4 py-3"><Badge className={`gap-1 ${sc.cls}`}><SIcon className="h-3 w-3" />{sc.label}</Badge></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <Button variant="outline" size="sm" className="h-8 border-[color:var(--mk-border)] text-[color:var(--mk-text-2)] hover:text-[color:var(--mk-text)]" onClick={() => openPreview(it.code)} data-testid={`preview-eform-${it.code}`}><Eye className="mr-1 h-3.5 w-3.5" /> Preview</Button>
                        <Button variant="outline" size="sm" className="h-8 border-[color:var(--mk-primary)]/40 text-[color:var(--mk-primary-2)] hover:bg-[rgba(176,141,87,0.12)]" disabled={downloading === it.code} onClick={() => downloadPdf(it.code)} data-testid={`download-eform-${it.code}`}><Download className="mr-1 h-3.5 w-3.5" /> {downloading === it.code ? "…" : "DRAFT"}</Button>
                        <Button variant="ghost" size="sm" className={`h-8 ${it.filed ? "text-[#6FB89A]" : "text-[color:var(--mk-muted)] hover:text-[color:var(--mk-text)]"}`} onClick={() => toggleFiled(it)} data-testid={`mark-filed-${it.code}`}><CheckCircle2 className="mr-1 h-3.5 w-3.5" /> {it.filed ? "Filed" : "Mark filed"}</Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* E-form preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className={`max-h-[90vh] max-w-2xl overflow-y-auto ${dialogSurface}`} data-testid="eform-preview-dialog">
          {preview && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <DialogTitle className="font-display">Form {preview.code}</DialogTitle>
                    <DialogDescription>{preview.title} · {preview.authority} · {preview.financial_year}</DialogDescription>
                  </div>
                  <span className="flex items-center gap-1 rounded-md border border-[rgba(176,141,87,0.4)] bg-[rgba(176,141,87,0.14)] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-[color:var(--mk-primary-2)]"><Stamp className="h-3.5 w-3.5" /> Draft</span>
                </div>
              </DialogHeader>
              <div className="relative space-y-5 py-1">
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
                  <span className="rotate-[-24deg] select-none text-6xl font-bold uppercase tracking-widest text-[color:var(--mk-primary)] opacity-[0.06]">DRAFT</span>
                </div>
                {preview.sections.map((sec, idx) => (
                  <section key={idx} className="relative">
                    <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[color:var(--mk-primary-2)]">{sec.heading}</div>
                    {sec.type === "kv" ? (
                      <div className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
                        {sec.rows.map(([label, value], i) => (
                          <div key={i} className="flex justify-between gap-3 border-b border-dashed border-[color:var(--mk-border)] py-1.5">
                            <span className="text-xs text-[color:var(--mk-muted)]">{label}</span>
                            <span className="text-right text-xs font-medium text-[color:var(--mk-text)]">{value}</span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="overflow-x-auto rounded-lg border border-[color:var(--mk-border)]">
                        <table className="w-full text-xs">
                          <thead className="bg-[rgba(16,27,43,0.6)]"><tr>{sec.columns.map((c) => <th key={c} className="px-3 py-2 text-left font-semibold text-[color:var(--mk-text-2)]">{c}</th>)}</tr></thead>
                          <tbody>{sec.data.map((row, ri) => <tr key={ri} className="border-t border-[color:var(--mk-border)]">{row.map((cell, ci) => <td key={ci} className="px-3 py-2 text-[color:var(--mk-text)]">{cell}</td>)}</tr>)}</tbody>
                        </table>
                      </div>
                    )}
                  </section>
                ))}
              </div>
              <DialogFooter>
                <Button variant="outline" className="border-[color:var(--mk-border)]" onClick={() => setPreviewOpen(false)}>Close</Button>
                <Button className={primaryBtn} onClick={() => downloadPdf(preview.code)} data-testid="preview-download-button"><Download className="mr-2 h-4 w-4" /> Download DRAFT PDF</Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ---------------- small helpers ---------------- */
function Field({ label, children, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label className="text-xs uppercase tracking-[0.12em] text-[color:var(--mk-muted)]">{label}</Label>
      {children}
    </div>
  );
}

function SelectBox({ value, onChange, options, placeholder = "Select", testid }) {
  const opts = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  return (
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>{opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
    </Select>
  );
}
